import React, { useEffect, useState } from 'react';
import Table from '../../shared/components/layout/Table';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import Button from '../../shared/components/ui/Button';
import Header from '../../shared/components/layout/Header';
import Search from '../../shared/components/ui/Search';
import { transformData } from '../../super-admin/utils/transformData';
import { useParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import Modal from '../../shared/components/ui/Modal';
import { CreateRateList } from '../components/forms/CreateRateList';
import { CreatePLCRateList } from '../components/forms/CreatePLCRateList';
import { get, set, useForm } from 'react-hook-form';
import EditPricing from '../components/forms/EditPricing';
import Cookies from 'js-cookie';
import {
  getAllPricingByProjectId,
  getSinglePricingById,
  getPlc,
  editPricing,
  getUniversalMeasurementByProject
} from '../services/api/implementation/impl';
import toast from 'react-hot-toast';
import FormInput from '../../shared/components/ui/FormInput';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import { Plus, Home, Ruler, ChevronRight, Check } from 'lucide-react';
import editIcon from '../../shared/assets/svg/edit.svg';
import { useFocusEffect } from 'react-router-dom';
import { createPortal } from 'react-dom';

export const RateList = ({
  projectName,
  filterPortalEl = null,
  anchorFiltersInPricingSubtab = false
}) => {
  const userId = Cookies.get('userId');
  const { id: projectId } = useParams();
  const { register } = useForm();

  const rawMeasurementUnit = Cookies.get('measurement_type');
  const measurementType =
    !rawMeasurementUnit || rawMeasurementUnit === 'null' || rawMeasurementUnit === 'undefined'
      ? 'sq/ft'
      : rawMeasurementUnit;
  const measurementTypeWithoutSlash = measurementType.replace('/', '');
  const { collapsed } = useSidebar();
  // 1. Local state for selected accommodation type, default = "ALL"
  const [selectedAccommodationType, setSelectedAccommodationType] = useState('ALL');
  const [rateUnit, setRateUnit] = useState(
    rawMeasurementUnit === 'sq/ft' || rawMeasurementUnit === 'sq/mt' ? rawMeasurementUnit : 'sq/ft'
  );
  const [selectedEditType, setSelectedEditType] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unitPricingList, setUnitPricingList] = useState([]);
  const [accommodationPricingList, setAccommodationPricingList] = useState([]);
  const [allPricingList, setAllPricingList] = useState([]);
  const [currentPriceingId, setCurrentPriceingId] = useState(null);

  const [isOpenRateList, setIsOpenRateList] = useState(false);
  const [rateListInitialSubCategory, setRateListInitialSubCategory] = useState('');
  const [selectedTableType, setSelectedTableType] = useState('');
  const [isEditingRateList, setIsEditingRateList] = useState(false);

  const [blockDropdownOptions, setBlockDropdownOptions] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState('');
  const [currentBlockWithPLC, setCurrentBlockWithPLC] = useState([]);
  const [selectedBlockName, setSelectedBlockName] = useState('');

  const [isPricingEmpty, setIsPricingEmpty] = useState(false);
  const [isOpenPLCRateList, setIsOpenPLCRateList] = useState(false);
  const [pricingType, setPricingType] = useState('all');

  const [plcMode, setPlcMode] = useState(null);

  // ------------------ Query Calls ------------------
  const {
    data: getAllPricingList,
    refetch: refetchAllPricingList,
    isLoading: isPricingListLoading,
    isSuccess: isPricingListSuccess
  } = useQuery({
    queryKey: ['getAllPricingList', projectId],
    queryFn: () => getAllPricingByProjectId(projectId),
    enabled: !!projectId
  });

  const showCreateRateListCards =
    isPricingListSuccess && (getAllPricingList == null || getAllPricingList.length === 0);

  const { data: pricingData, refetch: refetchPricingData } = useQuery({
    queryKey: ['pricingData', currentPriceingId],
    queryFn: () => getSinglePricingById(parseInt(currentPriceingId), projectId),
    enabled: !!currentPriceingId
  });

  const { data: PLCByProjectId, refetch: refetchPLCProject } = useQuery({
    queryKey: ['PLCByProjectId', projectId],
    queryFn: () => getPlc(),
    enabled: !!projectId
  });

  const { data: universalMeasurements = [] } = useQuery({
    queryKey: ['universalMeasurementsByProject', projectId],
    queryFn: () => getUniversalMeasurementByProject(projectId),
    enabled: !!projectId
  });

  useEffect(() => {
    refetchPricingData();
    refetchAllPricingList();
    refetchPLCProject();
  }, []);

  // ------------------ Effects ------------------

  // Track page view
  useEffect(() => {
    mixpanel.track('Rate List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (getAllPricingList === undefined) return;
    setIsPricingEmpty(!getAllPricingList.length);
  }, [getAllPricingList]);

  useEffect(() => {
    // If we have no pricing records => 'all'
    // If we have multiple modes (sqft + accommodation), use 'all' to show both
    // Otherwise use the single mode present
    if (isPricingEmpty) {
      setPricingType('all');
    } else if (getAllPricingList && getAllPricingList.length > 0) {
      const modes = [...new Set(getAllPricingList.map((p) => p.mode).filter(Boolean))];
      if (modes.length > 1) {
        setPricingType('all');
        setSelectedTableType(modes[0] || 'sqft');
      } else {
        const singleMode = modes[0] || getAllPricingList[0].mode;
        setPricingType(singleMode);
        setSelectedTableType(singleMode);
      }
    }
  }, [isPricingEmpty, getAllPricingList, refetchAllPricingList]);

  // **Filter** pricing data by selectedAccommodationType when it changes (or pricing data changes)
  useEffect(() => {
    if (getAllPricingList == null) {
      setUnitPricingList([]);
      setAccommodationPricingList([]);
      setAllPricingList([]);
      return;
    }
    if (getAllPricingList) {
      const filteredPricingList =
        selectedAccommodationType === 'ALL'
          ? getAllPricingList
          : getAllPricingList.filter((item) => {
              return (
                item.accommodation?.accomodationType === selectedAccommodationType.toUpperCase() ||
                false
              );
            });

      // Separate by mode (kept for any legacy use)
      const accommodationData = filteredPricingList.filter((item) => item.mode === 'accommodation');
      const sqftData = filteredPricingList.filter((item) => item.mode === 'sqft');

      setUnitPricingList(
        sqftData.map((el) =>
          transformData(
            handleUnit(
              el,
              selectedAccommodationType === 'ALL'
                ? el.accommodation?.accomodationType
                : selectedAccommodationType
            ),
            unitRateListFormat
          )
        )
      );
      setAccommodationPricingList(
        accommodationData.map((el) =>
          transformData(
            handleAcc(
              el,
              selectedAccommodationType === 'ALL'
                ? el.accommodation?.accomodationType
                : selectedAccommodationType
            ),
            accRatelistFormat
          )
        )
      );
      // Unified list: one row per pricing with pricingType column (sqft | accommodation | um name)

      // Reverse-converts stored price (per cookie unit) back to price-per-um-unit for display
      // UM type always matches cookie type (enforced by dropdown filter)
      // e.g. stored ₹100/sq/ft, 1 tago = 10 sq/ft → ₹100 × 10 = ₹1000/tago
      const reverseConvertUmPrice = (storedPrice, um) => {
        const perUnit = parseFloat(um?.per_unit) || 1;
        return parseFloat((storedPrice * perUnit).toFixed(8));
      };

      const accType = (el) =>
        selectedAccommodationType === 'ALL' ? el.accommodation?.accomodationType : selectedAccommodationType;
      const getBuildUpArea = (el) => el.accommodation?.buildUpArea ?? el.accommodation?.build_up_area ?? '—';
      const getPlotArea = (el) => el.accommodation?.plotArea ?? el.accommodation?.plot_area ?? '—';
      const getUmForEl = (el) => {
        if (!el.universal_measurement_id) return null;
        return universalMeasurements.find((u) => u.id === el.universal_measurement_id) || null;
      };

      const unified = filteredPricingList.map((el) => {
        const type = accType(el);
        if (el.mode === 'sqft') {
          const u = handleUnit(el, type);
          const um = getUmForEl(el);
          if (um) {
            // Override prices with reverse-converted UM values
            const isIndependent = type === 'INDEPENDENT';
            if (isIndependent) {
              u.plote_price = `₹${formattedNumberToIndian(reverseConvertUmPrice(el.plote_price || 0, um))}`;
              u.construction_price = `₹${formattedNumberToIndian(reverseConvertUmPrice(el.construction_price || 0, um))}`;
            } else {
              u.price = `₹${formattedNumberToIndian(reverseConvertUmPrice(el.price || 0, um))}`;
            }
          }
          return {
            pricingType: um ? `${um.name} (${um.measurement_type})` : 'sqft',
            ...u,
            buildUpArea: getBuildUpArea(el),
            plotArea: getPlotArea(el)
          };
        }
        const a = handleAcc(el, type);
        return { pricingType: 'accommodation', ...a, loading_factor: el.loading_factor ?? '—' };
      });
      setAllPricingList(unified);
    }
  }, [getAllPricingList, selectedAccommodationType, universalMeasurements]);

  useEffect(() => {
    refetchAllPricingList();
  }, [isOpenRateList]);

  useEffect(() => {
    if (currentPriceingId) {
      refetchPricingData();
    }
  }, [currentPriceingId]);

  // --------------- PLC / Blocks Setup ---------------
  useEffect(() => {
    if (PLCByProjectId && PLCByProjectId.length > 0) {
      const filteredPLCs = PLCByProjectId.filter((plc) =>
        plc.plc_block_mapping.some((block) => block.block.projectId === parseInt(projectId))
      );

      const blocks = filteredPLCs.flatMap((plc) => plc.plc_block_mapping);
      const uniqueBlocks = Array.from(new Set(blocks?.map((block) => block.block_id)))?.map((id) =>
        blocks.find((block) => block.block_id === id)
      );

      const options = uniqueBlocks?.map((item) => ({
        value: item.block.id,
        label: item.block.name
      }));

      // Add "All" option at the beginning
      const optionsWithAll = [{ value: 'all', label: 'All' }, ...options];
      setBlockDropdownOptions(optionsWithAll);

      if (optionsWithAll.length > 0) {
        setSelectedBlockId('all');
      }
    }
  }, [PLCByProjectId, projectId]);

  useEffect(() => {
    if (PLCByProjectId && selectedBlockId) {
      // Handle "All" option
      if (selectedBlockId === 'all') {
        const PLCList = PLCByProjectId.flatMap((plc) => {
          return plc.plc_block_mapping
            .filter((blockMapping) => blockMapping.block.projectId === parseInt(projectId))
            .map((blockMapping) => ({
              id: plc.id,
              name: plc.name,
              plcRate: plc.price || null,
              mode: plc.mode || null,
              blockId: blockMapping.block.id,
              blockName: blockMapping.block.name
            }));
        });

        const uniqueData = Object.values(
          PLCList.reduce((acc, curr) => {
            const key = `${curr.id}-${curr.blockId}`;
            acc[key] = curr;
            return acc;
          }, {})
        );

        const transformedPLCList = uniqueData.map((item) =>
          transformData(item, ['name', 'plcRate', 'id', 'mode', 'blockId', 'blockName'])
        );

        setCurrentBlockWithPLC(transformedPLCList);
        setSelectedBlockName('All Blocks');
      } else {
        // Handle specific block selection
        const filteredBlock = PLCByProjectId.flatMap((plc) =>
          plc.plc_block_mapping.filter((block) => block.block.id === parseInt(selectedBlockId))
        );

        if (filteredBlock.length > 0) {
          const block = filteredBlock[0];
          const blockName = block.block?.name || '';

          const PLCList = PLCByProjectId.flatMap((plc) => {
            return plc.plc_block_mapping
              .filter((blockMapping) => blockMapping.block.id === parseInt(selectedBlockId))
              .map((blockMapping) => ({
                id: plc.id,
                name: plc.name,
                plcRate: plc.price || null,
                mode: plc.mode || null
              }));
          });

          const uniqueData = Object.values(
            PLCList.reduce((acc, curr) => {
              acc[curr.id] = curr;
              return acc;
            }, {})
          );

          const transformedPLCList = uniqueData.map((item) =>
            transformData(item, ['name', 'plcRate', 'id', 'mode'])
          );

          setCurrentBlockWithPLC(transformedPLCList);
          setSelectedBlockName(blockName);
        }
      }
    }
  }, [selectedBlockId, PLCByProjectId, projectId]);

  useEffect(() => {
    if (currentBlockWithPLC.length > 0) {
      const modes = currentBlockWithPLC.map((plc) => plc.mode);
      if (modes.includes('percent')) {
        setPlcMode('percent');
      } else if (modes.includes('numeric')) {
        setPlcMode('numeric');
      }
    }
  }, [currentBlockWithPLC]);

  // ------------------ Handlers ------------------
  const formattedNumberToIndian = (number) => {
    return new Intl.NumberFormat('en-IN').format(number);
  };

  const formattedCurrencyToIndianTwoDecimals = (number) => {
    const parsed = Number.parseFloat(number);
    const safeValue = Number.isFinite(parsed) ? parsed : 0;
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(safeValue);
  };

  // Handle rate unit conversion
  const convertRate = (value, fromUnit, toUnit) => {
    if (!value || value === '---') return value;

    // Remove currency symbol and commas
    const cleanValue = value.replace(/[₹,]/g, '').trim();
    const numValue = parseFloat(cleanValue);
    if (isNaN(numValue)) return value;

    const normalizeUnit = (unit) => {
      if (!unit) return 'sq/ft';
      const normalized = unit.toLowerCase().replace(/\s+/g, '');
      if (normalized === 'sqft' || normalized === 'sq/ft' || normalized === 'sqfeet')
        return 'sq/ft';
      if (normalized === 'sqmt' || normalized === 'sq/mt' || normalized === 'sqm') return 'sq/mt';
      if (normalized === 'guntha' || normalized === 'gunta') return 'guntha';
      if (normalized === 'cents' || normalized === 'cent') return 'cents';
      return unit;
    };

    const normalizedFromUnit = normalizeUnit(fromUnit);
    const normalizedToUnit = normalizeUnit(toUnit);

    if (normalizedFromUnit === normalizedToUnit) return value;

    // How many sq/ft are in each unit
    const sqFtPerUnit = {
      'sq/ft': 1,
      'sq/mt': 10.764, // 1 sq/mt = 10.764 sq/ft
      guntha: 1089, // 1 guntha = 1089 sq/ft
      cents: 435.6 // 1 cent = 435.6 sq/ft
    };

    // For RATES (price per unit area): conversion is inverted compared to area
    // e.g., ₹5000/sq/ft → ₹53,820/sq/mt (multiply by 10.764)
    // Because 1 sq/mt contains 10.764 sq/ft, so the price per sq/mt is higher
    const ratePerSqFt = numValue / sqFtPerUnit[normalizedFromUnit];
    const convertedValue = ratePerSqFt * sqFtPerUnit[normalizedToUnit];

    return `₹${formattedNumberToIndian(convertedValue.toFixed(2))}`;
  };

  const handleAcc = (item, selectedAccommodationType) => {
    const buildUpArea = item.accommodation?.buildUpArea ?? item.accommodation?.build_up_area ?? '—';
    const plotArea = item.accommodation?.plotArea ?? item.accommodation?.plot_area ?? '—';
    if (selectedAccommodationType === 'INDEPENDENT') {
      return {
        id: item.id,
        accommodation: item.accommodation.name,
        accommodationType: item.accommodation?.accomodationType || selectedAccommodationType,
        buildUpArea,
        plotArea,
        plote_price: `₹${formattedCurrencyToIndianTwoDecimals(item.plote_price)}`,
        construction_price: `₹${formattedCurrencyToIndianTwoDecimals(item.construction_price)}`
      };
    } else {
      return {
        id: item.id,
        accommodation: item.accommodation.name,
        accommodationType: item.accommodation?.accomodationType || selectedAccommodationType,
        buildUpArea,
        plotArea,
        price: `₹${formattedCurrencyToIndianTwoDecimals(item.price)}`
      };
    }
  };

  const handleUnit = (item, selectedAccommodationType) => {
    if (selectedAccommodationType === 'INDEPENDENT') {
      return {
        id: item.id,
        accommodation: item.accommodation.name,
        accommodationType: item.accommodation?.accomodationType || selectedAccommodationType,
        plote_price: `₹${formattedNumberToIndian(item.plote_price)}`,
        construction_price: `₹${formattedNumberToIndian(item.construction_price)}`,
        loading_factor: item.loading_factor
      };
    } else {
      return {
        id: item.id,
        accommodation: item.accommodation.name,
        accommodationType: item.accommodation?.accomodationType || selectedAccommodationType,
        price: `₹${formattedNumberToIndian(item.price)}`,
        loading_factor: item.loading_factor
      };
    }
  };

  const unitRateListFormat = [
    'accommodationType',
    'accommodation',
    'price',
    'plote_price',
    'construction_price',
    'loading_factor',
    'actions',
    'id'
  ];
  const accRatelistFormat = [
    'accommodationType',
    'accommodation',
    'buildUpArea',
    'plotArea',
    'price',
    'plote_price',
    'construction_price',
    'loading_factor',
    'actions',
    'id'
  ];
  const allRateListFormat = [
    'accommodationType',
    'accommodation',
    'price',
    'plote_price',
    'construction_price',
    'loading_factor',
    'actions',
    'id'
  ];

  const { mutate: editPricingData } = useMutation({
    mutationFn: (payload) => {
      const formattedPayload = Array.isArray(payload)
        ? payload
        : [
          {
            id: currentPriceingId,
            project_id: parseInt(projectId),
            accommodation_id: pricingData?.accommodation?.id,
            mode: payload.mode ?? selectedTableType,
            ...(payload.universal_measurement_id
              ? { universal_measurement_id: payload.universal_measurement_id }
              : {}),
            ...(pricingData?.accommodation?.accomodationType === 'INDEPENDENT'
              ? {
                plote_price: payload.plote_price ?? payload.plot_price ?? 0,
                construction_price: payload.construction_price ?? 0,
                loading_factor: payload.loading_factor ?? 0
              }
              : {
                price: payload.price ?? payload.rate ?? 0,
                loading_factor: payload.loading_factor ?? 0
              })
          }
        ];

      return editPricing(currentPriceingId, formattedPayload);
    },
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      setCurrentPriceingId(null);
      setIsEditingRateList(false);
      refetchAllPricingList();
      toast.success('Pricing edited successfully');
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error editing pricing', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error editing pricing');
    }
  });

  const pricingEditHandler = (data) => {
    if (Object.prototype.hasOwnProperty.call(data, 'rate')) {
      data.rate = parseInt(data.rate);
    }
    setIsSubmitting(true);
    editPricingData(data);
  };

  const onEditClick = (id) => {
    setCurrentPriceingId(id);
    setIsEditingRateList(true);
  };

  const openRateListModal = (initialSubCategory = '') => {
    setRateListInitialSubCategory(initialSubCategory);
    setIsOpenRateList(true);
  };

  const closeRateListModal = () => {
    setIsOpenRateList(false);
    setRateListInitialSubCategory('');
  };

  const handleTableTypeChange = (e) => {
    setSelectedTableType(e.target.value);
  };

  const handleBlockNameChange = (e) => {
    setSelectedBlockId(e.target.value);
  };

  const handleAccommodationTypeChange = (e) => {
    setSelectedAccommodationType(e.target.value);
  };

  const onPlCUpdateSuccess = () => {
    setIsOpenPLCRateList(false);
    refetchPLCProject();
  };

  const onCreateSuccess = () => {
    closeRateListModal();
    refetchAllPricingList(projectId);
  };

  let tableHeadersSqFt = [];
  if (selectedAccommodationType === 'ALL') {
    tableHeadersSqFt = [
      'Accommodation Type',
      'Accommodation Name',
      'Plot Price',
      'Construction Price',
      'Loading factor',
      'Action'
    ];
  } else if (selectedAccommodationType === 'INDEPENDENT') {
    tableHeadersSqFt = [
      'Accommodation Type',
      'Accommodation Name',
      'Plot Price',
      'Construction Price',
      'Loading factor',
      'Action'
    ];
  } else {
    tableHeadersSqFt = [
      'Accommodation Type',
      'Accommodation Name',
      'Rates',
      'Loading factor',
      'Action'
    ];
  }

  let tableHeadersAccommodation = [];
  if (selectedAccommodationType === 'ALL') {
    tableHeadersAccommodation = [
      'Accommodation Type',
      'Accommodation Name',
      'Plot Price',
      'Construction Price',
      'Action'
    ];
  } else if (selectedAccommodationType === 'INDEPENDENT') {
    tableHeadersAccommodation = [
      'Accommodation Type',
      'Accommodation Name',
      'Plot Price',
      'Construction Price',
      'Action'
    ];
  } else {
    tableHeadersAccommodation = ['Accommodation Type', 'Accommodation Name', 'Rates', 'Action'];
  }

  const handleSelectAccommodationFilter = (type) => {
    setSelectedAccommodationType(type);
  };

  const accommodationTypeFilterPills = (
    <div className="flex rounded-full border border-gray-200 bg-gray-100/90 p-1 shadow-sm">
      {[
        { value: 'ALL', label: 'All' },
        { value: 'GROUP', label: 'Group' },
        { value: 'INDEPENDENT', label: 'Independent' }
      ].map(({ value, label }) => (
        <button
          key={value}
          type="button"
          onClick={() => handleSelectAccommodationFilter(value)}
          className={`px-5 py-2.5 text-sm font-medium transition-all duration-200 rounded-full ${
            selectedAccommodationType === value
              ? 'bg-primary text-white'
              : 'bg-transparent text-gray-800 hover:bg-gray-200/60'
          }`}
          style={
            selectedAccommodationType === value
              ? { boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)' }
              : undefined
          }>
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="w-full relative">
      {isOpenRateList && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Create Rate List'}
          onClose={closeRateListModal}
          width={80}>
          <CreateRateList
            projectName={projectName}
            createSuccess={onCreateSuccess}
            pricingType={pricingType}
            onClose={closeRateListModal}
            initialSubCategory={rateListInitialSubCategory}
          />
        </Modal>
      )}
      {isOpenPLCRateList && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Create PLC Rate List'}
          onClose={() => setIsOpenPLCRateList(false)}
          width={80}>
          <CreatePLCRateList
            projectName={projectName}
            createSuccess={onPlCUpdateSuccess}
            plcList={currentBlockWithPLC}
            blockName={selectedBlockName}
            selectedBlockId={selectedBlockId}
            onClose={() => setIsOpenPLCRateList(false)}
            initialMode={plcMode}
          />
        </Modal>
      )}
      {isEditingRateList && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Edit Rate List'}
          onClose={() => {
            setIsEditingRateList(false);
            setCurrentPriceingId(null);
          }}
          width={80}>
          <EditPricing
            isSubmitting={isSubmitting}
            handleEdit={pricingEditHandler}
            selectedTableType={selectedTableType}
            singlePricingData={pricingData}
            selectedAccommodationType={selectedEditType}
            pricingType={pricingType}
            onClose={() => {
              setIsEditingRateList(false);
            }}
          />
        </Modal>
      )}

      {/* Grid */}
      <GridWrapper width={100}>
        <div className="w-full overflow-clip bg-gray-100">
          {anchorFiltersInPricingSubtab &&
            filterPortalEl &&
            createPortal(accommodationTypeFilterPills, filterPortalEl)}
          {(!anchorFiltersInPricingSubtab || !filterPortalEl) && (
            <div className="mt-5 flex w-full flex-wrap items-center justify-end gap-4">
              {accommodationTypeFilterPills}
            </div>
          )}
          {/*
            Rate Unit dropdown (hidden per product request; table still uses measurement cookie default)
            {(allPricingList.some((r) => r.pricingType === 'sqft') || measurementTypeWithoutSlash === 'sqmt') && (
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">Rate Unit:</span>
                <select
                  value={rateUnit}
                  onChange={(e) => setRateUnit(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none bg-white text-gray-900 cursor-pointer">
                  <option value="sq/ft">sq/ft</option>
                  <option value="sq/mt">sq/mt</option>
                  <option value="guntha">guntha</option>
                  <option value="cents">cents</option>
                </select>
              </div>
            )}
            */}

          {!showCreateRateListCards && (
          <div className="fixed bottom-8 right-8 z-50">
            <Button
              displayType="primary"
              onClick={() => {
                mixpanel.track('Add Rate List Button Clicked', {
                  user_id: userId,
                  timestamp: new Date().toISOString()
                });
                openRateListModal('');
              }}
              customStyle={{
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}>
              <Plus size={24} color="white" />
            </Button>
          </div>
          )}

          {/* Single table with Pricing Type column, or empty-state model cards when project has no rates */}
          <div
            className={`rate-list-table-container flex flex-col gap-4 h-[calc(100vh-17.5rem)] overflow-auto ${
              anchorFiltersInPricingSubtab && filterPortalEl ? 'mt-3' : 'mt-2'
            }`}>
                <style>{`
                  .rate-list-table-container .table-body { position: static !important; overflow-y: visible !important; max-height: none !important; height: auto !important; top: auto !important; padding-bottom: 0 !important; }
                  .rate-list-table-container .table-container { height: auto !important; }
                  .rate-list-table-container > div { height: auto !important; }
                `}</style>
                {isPricingListLoading ? (
                  <div className="flex min-h-[16rem] w-full flex-col items-center justify-center rounded-lg border border-gray-200 bg-white py-16">
                    <div
                      className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent"
                      aria-hidden
                    />
                    <p className="mt-4 text-sm text-gray-600">Loading pricing…</p>
                  </div>
                ) : showCreateRateListCards ? (
                  <div className="w-full overflow-hidden rounded-[24px] border border-gray-200 bg-white shadow-[0_10px_28px_rgba(15,23,42,0.08),0_2px_6px_rgba(15,23,42,0.06)]">
                    <div className="border-b border-gray-200 bg-[#E9F5F6] px-7 py-6">
                      <h2 className="text-xl font-bold text-gray-800">Create Rate List</h2>
                      <p className="mt-1 text-sm text-gray-600">Choose your pricing model structure</p>
                    </div>
                    <div className="grid gap-6 p-7 md:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => {
                          mixpanel.track('Create Rate List Model Selected', {
                            model: 'accommodation',
                            user_id: userId,
                            timestamp: new Date().toISOString()
                          });
                          openRateListModal('accommodation');
                        }}
                        className="group relative flex flex-col overflow-hidden rounded-[20px] border border-gray-200 bg-white text-left shadow-[0_4px_14px_rgba(15,23,42,0.08)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_26px_rgba(15,23,42,0.14)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                        <div className="flex flex-1 flex-col p-6">
                          <div className="flex gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-gradient-to-b from-gray-50 to-gray-100 shadow-inner">
                              <Home className="h-5 w-5 text-gray-700" strokeWidth={2} />
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-bold text-gray-900">
                                Accommodation Based (Fixed Pricing)
                              </h3>
                              <p className="mt-1 text-sm text-gray-600">
                                Define pricing based on unit types like 1BHK, 2BHK, 3BHK, Villas, etc
                              </p>
                            </div>
                          </div>
                          <ul className="mt-5 space-y-2.5 text-sm text-gray-700">
                            {[
                              'Group by accommodation type',
                              'Per-unit pricing structure',
                              'Ideal for apartments & villas'
                            ].map((line) => (
                              <li key={line} className="flex items-start gap-2">
                                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100">
                                  <Check className="h-3 w-3 text-gray-500" strokeWidth={3} />
                                </span>
                                {line}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50/70 px-6 py-4">
                          <span className="text-xs font-bold tracking-wide text-gray-800">
                            SELECT THIS MODEL
                          </span>
                          <span className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 shadow-sm transition-all duration-200 group-hover:translate-x-0.5 group-hover:bg-primary group-hover:text-white group-hover:border-primary">
                            <ChevronRight className="h-4 w-4" />
                          </span>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          mixpanel.track('Create Rate List Model Selected', {
                            model: measurementTypeWithoutSlash,
                            user_id: userId,
                            timestamp: new Date().toISOString()
                          });
                          openRateListModal(measurementTypeWithoutSlash);
                        }}
                        className="group relative flex flex-col overflow-hidden rounded-[20px] border border-gray-200 bg-white text-left shadow-[0_4px_14px_rgba(15,23,42,0.08)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_26px_rgba(15,23,42,0.14)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                        <div className="flex flex-1 flex-col p-6">
                          <div className="flex gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-gradient-to-b from-gray-50 to-gray-100 shadow-inner">
                              <Ruler className="h-5 w-5 text-gray-700" strokeWidth={2} />
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-bold text-gray-900">
                                {measurementType.replace('/', '-')} Based
                              </h3>
                              <p className="mt-1 text-sm text-gray-600">
                                Define pricing based on square footage with flexible per {measurementType}{' '}
                                rates
                              </p>
                            </div>
                          </div>
                          <ul className="mt-5 space-y-2.5 text-sm text-gray-700">
                            {[
                              'Calculate by square footage',
                              'Flexible area-based pricing',
                              'Perfect for plots & custom units'
                            ].map((line) => (
                              <li key={line} className="flex items-start gap-2">
                                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100">
                                  <Check className="h-3 w-3 text-gray-500" strokeWidth={3} />
                                </span>
                                {line}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50/70 px-6 py-4">
                          <span className="text-xs font-bold tracking-wide text-gray-800">
                            SELECT THIS MODEL
                          </span>
                          <span className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 shadow-sm transition-all duration-200 group-hover:translate-x-0.5 group-hover:bg-primary group-hover:text-white group-hover:border-primary">
                            <ChevronRight className="h-4 w-4" />
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>
                ) : (
                <div className="w-full relative rounded-t-lg">
                  <table className="text-left w-full">
                    <thead className="bg-gray-200 !text-gray-600 w-full sticky top-0 z-20">
                      <tr className="font-normal h-[3rem] w-full">
                        <th className="px-4 py-2 border-b font-normal">Pricing Type</th>
                        <th className="px-4 py-2 border-b font-normal w-[180px]">
                          Accommodation Type
                        </th>
                        <th className="px-4 py-2 border-b font-normal">Accommodation Name</th>
                        <th className="px-4 py-2 border-b font-normal">Build up area</th>
                        <th className="px-4 py-2 border-b font-normal">Plot area</th>
                        {selectedAccommodationType === 'ALL' ? (
                          <>
                            <th className="px-4 py-2 border-b font-normal">Plot Price</th>
                            <th className="px-4 py-2 border-b font-normal">Construction Price</th>
                            <th className="px-4 py-2 border-b font-normal">Rates({rateUnit})</th>
                            <th className="px-4 py-2 border-b font-normal">Loading factor</th>
                          </>
                        ) : selectedAccommodationType === 'INDEPENDENT' ? (
                          <>
                            <th className="px-4 py-2 border-b font-normal">Plot Price</th>
                            <th className="px-4 py-2 border-b font-normal">Construction Price</th>
                            <th className="px-4 py-2 border-b font-normal">Loading factor</th>
                          </>
                        ) : (
                          <>
                            <th className="px-4 py-2 border-b font-normal">Rates({rateUnit})</th>
                            <th className="px-4 py-2 border-b font-normal">Loading factor</th>
                          </>
                        )}
                        <th className="px-4 py-2 border-b font-normal">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(allPricingList) && allPricingList.length > 0 ? (
                        allPricingList.map((item, idx) => (
                          <tr
                            key={item.id || idx}
                            className="border-b hover:bg-gray-50 bg-white h-[3.5rem]">
                            <td className="px-4 py-2 capitalize">
                              {item.pricingType || '—'}
                            </td>
                            <td className="px-4 py-2 w-[180px]">
                              {selectedAccommodationType === 'ALL'
                                ? item.accommodationType
                                : idx === 0
                                  ? selectedAccommodationType
                                  : ''}
                            </td>
                            <td className="px-4 py-2">{item.accommodation || '---'}</td>
                            <td className="px-4 py-2">{item.buildUpArea ?? '—'}</td>
                            <td className="px-4 py-2">{item.plotArea ?? '—'}</td>
                            {selectedAccommodationType === 'ALL' ? (
                              <>
                                <td className="px-4 py-2">{item.plote_price || '---'}</td>
                                <td className="px-4 py-2">{item.construction_price || '---'}</td>
                                <td className="px-4 py-2">
                                  {item.pricingType === 'sqft' && item.price
                                    ? convertRate(item.price, measurementType, rateUnit)
                                    : (item.price || '---')}
                                </td>
                                <td className="px-4 py-2">{item.loading_factor ?? '---'}</td>
                              </>
                            ) : selectedAccommodationType === 'INDEPENDENT' ? (
                              <>
                                <td className="px-4 py-2">{item.plote_price || '---'}</td>
                                <td className="px-4 py-2">{item.construction_price || '---'}</td>
                                <td className="px-4 py-2">{item.loading_factor ?? '---'}</td>
                              </>
                            ) : (
                              <>
                                <td className="px-4 py-2">
                                  {item.pricingType === 'sqft' && item.price
                                    ? convertRate(item.price, measurementType, rateUnit)
                                    : (item.price || '---')}
                                </td>
                                <td className="px-4 py-2">{item.loading_factor ?? '---'}</td>
                              </>
                            )}
                            <td className="px-4 py-2">
                              <div className="flex items-center justify-start w-full relative gap-3">
                                <div
                                  className="cursor-pointer hover:scale-110 transition-all duration-300 action-button"
                                  onClick={(e) => {
                                    setSelectedTableType(item.pricingType);
                                    setSelectedEditType(item.accommodationType);
                                    e.stopPropagation();
                                    onEditClick(item.id);
                                  }}>
                                  <img src={editIcon} alt="edit__icon" />
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={
                              selectedAccommodationType === 'ALL'
                                ? 10
                                : selectedAccommodationType === 'INDEPENDENT'
                                  ? 9
                                  : 8
                            }
                            className="text-center py-10 align-middle">
                            <div className="flex flex-col items-center justify-center w-full h-full">
                              <h2 className="text-lg font-bold text-gray-800 mb-1">No Data Found</h2>
                              <p className="text-gray-600 text-sm">
                                No rates found for the selected accommodation type.
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                )}
          </div>
        </div>
      </GridWrapper>
    </div>
  );
};
